import { jsx } from 'react/jsx-runtime';
import cookie from 'js-cookie';
import React, { useState, useEffect } from 'react';
import { TrafficTypeCommon } from '@mirohq-internal/experimentation-core';
import { eventHub } from '@mirohq-internal/website-analytics';
import { cookieStorage } from '@mirohq-internal/website-utils';

let anonymousIdInterval;
var STATUSES;
(function(STATUSES) {
    STATUSES["loading"] = "loading";
    STATUSES["ready"] = "READY";
    STATUSES["error"] = "ERROR";
})(STATUSES || (STATUSES = {}));
const initialValue = {
    status: STATUSES.loading,
    isLoading: true
};
const SplitIoContext = /*#__PURE__*/ React.createContext([
    initialValue,
    null
]);
const SplitIoProvider = (props)=>{
    const { children, authorizationKey = process.env.NEXT_PUBLIC_SPLITIO_CLIENT_KEY, triesLimit = 20, attributes = {} } = props;
    const [splitIOState, setSplitIOState] = useState(initialValue);
    const [client, setClient] = useState(null);
    function isBot() {
        const userAgent = navigator.userAgent;
        const regexp = /bot|crawl|slurp|spider|mediapartners|go-http-client|apache|axios|ahc/i;
        return !!userAgent?.match(regexp);
    }
    useEffect(()=>{
        if (isBot()) {
            setSplitIOState({
                status: STATUSES.error,
                error: 'Crawler / bot detected'
            });
        } else {
            setSplitIOState({
                status: STATUSES.loading,
                error: ''
            });
            let tries = 0;
            anonymousIdInterval = window.setInterval(()=>{
                if (tries >= triesLimit) {
                    setSplitIOState({
                        status: STATUSES.error,
                        error: `Id cookie wasn't found`
                    });
                    clearInterval(anonymousIdInterval);
                    return;
                }
                const anonymousId = cookie.get('ajs_anonymous_id');
                if (typeof anonymousId === 'string' && anonymousId.length) {
                    clearInterval(anonymousIdInterval);
                    initSplitIo(anonymousId.replace(/"/g, ''));
                }
                tries++;
            }, 50);
        }
        async function initSplitIo(anonymousId) {
            // Init Split on client-side
            await (async ()=>{
                if (window.split) {
                    return;
                }
                // @ts-expect-error May need to update experimentation core package
                const ExperimentationClient = (await import('@mirohq-internal/experimentation-core')).ExperimentationClient;
                const experimentation = new ExperimentationClient();
                try {
                    const isMobile = window?.innerWidth < 992;
                    const defaultAttributes = {
                        prospect_user: !cookieStorage.get('ajs_user_id'),
                        is_bot: false,
                        is_mobile: isMobile,
                        is_desktop: !isMobile
                    };
                    await experimentation.initFactory({
                        config: {
                            authorizationKey: authorizationKey,
                            traffic: {
                                defaultTrafficType: 'anonymous_user',
                                anonymousUserKey: anonymousId
                            },
                            analytics: {
                                sendEvent: (...args)=>{
                                    if (!eventHub) return;
                                    eventHub.track(...args);
                                }
                            }
                        },
                        advancedSettings: {
                            storage: {
                                type: 'MEMORY'
                            }
                        },
                        attributes: {
                            ...defaultAttributes,
                            ...attributes
                        }
                    });
                    await experimentation.getClient().ready();
                    setSplitIOState({
                        status: STATUSES.ready,
                        error: ''
                    });
                    setClient(experimentation);
                } catch (_error) {
                    setSplitIOState({
                        status: STATUSES.error,
                        error: `Experimentation Client wasn't initialized`
                    });
                    setClient(experimentation);
                }
                window.split = experimentation;
            })();
        }
        return ()=>{
            clearInterval(anonymousIdInterval);
        };
    }, []);
    return jsx(SplitIoContext.Provider, {
        value: [
            {
                ...splitIOState,
                isLoading: splitIOState.status === STATUSES.loading
            },
            client
        ],
        children: children
    });
};
const useSplitIo = ()=>React.useContext(SplitIoContext);
const useTreatment = (name, attrs)=>{
    const [{ isLoading }, client] = useSplitIo();
    const [mockTreatments, setMockTreatments] = useState({});
    useEffect(()=>{
        const getMockTreatmentsFromUrl = ()=>{
            const treatments = {};
            const treatmentPrefix = 'split_exp_';
            if (location?.search) {
                const searchParams = new URLSearchParams(location.search);
                searchParams.forEach((value, param)=>{
                    if (param.startsWith(treatmentPrefix)) {
                        const key = param.slice(treatmentPrefix.length);
                        treatments[key] = value;
                    }
                });
                setMockTreatments(treatments);
            }
        };
        if (typeof window !== 'undefined') {
            getMockTreatmentsFromUrl();
        }
    }, []);
    const getMockTreatment = (name)=>mockTreatments[name];
    const treatment = getMockTreatment(name) ?? client?.getTreatment(name, attrs);
    return [
        treatment,
        isLoading
    ];
};
function getUserIdFromCookie() {
    const userId = cookie.get('ajs_user_id');
    if (typeof userId !== 'string') {
        return;
    }
    return userId.replace(/"/g, '');
}
/**
 * Return a new client with traffic set to current user id
 * @see {@link https://help.split.io/hc/en-us/articles/360058730852-Browser-SDK#instantiate-multiple-sdk-clients}
 */ function useUserSplitClient() {
    const [userClient, setUserClient] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [{ isLoading: isClientLoading }, client] = useSplitIo();
    useEffect(()=>{
        if (isClientLoading) {
            return;
        }
        const userId = getUserIdFromCookie();
        if (!userId) {
            setIsLoading(false);
            return;
        }
        const userClient = client?.getClient(userId, TrafficTypeCommon.USER);
        userClient.ready().then(()=>{
            setUserClient(userClient);
            setIsLoading(false);
        });
    }, [
        isClientLoading,
        client
    ]);
    return {
        isLoading,
        userClient
    };
}

export { SplitIoProvider, useSplitIo, useTreatment, useUserSplitClient };
